import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { push } from 'connected-react-router';
// Models
import { IPage } from '@root/models/Page';
// State
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectColumnValue, selectWorkspaceType } from 'app/store/ui/uiSelectors';
import { selectLastOpenedWorkspacePages, selectLastOpenedFilePages } from 'app/store/TODO2_app/app.selectors';
import { selectPagesInWorkspaceByPreEvent, selectFilteredDocumentPages } from 'app/store/page/page.selectors';
// MaterialUI
import Button from '@mui/material/Button';
// i18next
import { useTranslation } from 'react-i18next';
import { usePagesToolbarContext } from '../utils/usePagesToolbarContext';
import { Tooltip } from '@mui/material';
import { IconButton } from 'app/components/Mui';
import Icon from 'app/components/Icon';
import { selectNewDesignEnabled } from 'app/store/pagesToolbar/pagesToolbar.selectors';
import { CARD_WIDTH } from 'app/App.constants';

let timeout:any;

const GoToLastOpenedPage = () => {
  const { t } = useTranslation();

  const { listRef } = usePagesToolbarContext();

  const { pathname } = useLocation();
  const { caseId, documentId } = useParams<{ caseId:string; documentId:string }>();

  const isDocumentsPath = pathname.includes('documents');

  const dispatch = useAppDispatch();
  const newDesignEnabled = useAppSelector(selectNewDesignEnabled);
  const columnValue:string = useAppSelector(selectColumnValue);
  const workspaceType = useAppSelector(selectWorkspaceType);
  const lastOpenedFilePages:{ [key:number]:string } = useAppSelector(selectLastOpenedFilePages);
  const lastOpenedWorkspacePages:{ [key:number]:string } = useAppSelector(selectLastOpenedWorkspacePages);
  const pagesWithPreEvent:Array<IPage> | null = useAppSelector((state:any) => selectPagesInWorkspaceByPreEvent(state, { preEvent: true }));
  const pagesWithoutPreEvent:Array<IPage> | null = useAppSelector((state:any) => selectPagesInWorkspaceByPreEvent(state, { preEvent: false }));
  const documentPages = useAppSelector((state:any) => selectFilteredDocumentPages(state, { documentId: Number(documentId) })) || [];

  const pages = useMemo(() => {
    if ( isDocumentsPath ) return documentPages;
    const result:any = [];
    if ( pagesWithPreEvent && pagesWithPreEvent.length ){
      result.push({ type: 'placeholder', label: 'Pre-event' });
      for ( let i = 0; i < pagesWithPreEvent.length; i++ ){
        result.push({...pagesWithPreEvent[i], type: 'page', index: i });
      }
    }
    if ( pagesWithoutPreEvent && pagesWithoutPreEvent.length ){
      const index = pagesWithPreEvent ? pagesWithPreEvent.length : 0;
      result.push({ type: 'placeholder', label: 'Post-event' });
      for ( let i = 0; i < pagesWithoutPreEvent.length; i++ ){
        result.push({...pagesWithoutPreEvent[i], type: 'page', index: index + i });
      }
    }
    return result;
    // eslint-disable-next-line
  }, [pagesWithPreEvent, pagesWithoutPreEvent]);

  const lastOpenedPage = isDocumentsPath
    ? lastOpenedFilePages[Number(caseId)]
    : lastOpenedWorkspacePages[Number(caseId)]
  ;

  const handleGoToLastOpenedPage = () => {
    if ( !lastOpenedPage ) return;

    const [ docId, pageNum ] = lastOpenedPage.split(':').map(Number);

    if ( isDocumentsPath ){
      clearTimeout(timeout);
      let timeoutTime = 0;
      if ( docId !== Number(documentId) ){
        dispatch(push(`/admin/cases/${caseId}/documents/${docId}?pageNum=${pageNum}`));
        timeoutTime = 1000;
      }

      timeout = setTimeout(() => {
        if ( listRef.current && pages ){
          const foundedPageIndex = pages.findIndex((page:any) => page.pageNum === Number(pageNum));
          if ( typeof foundedPageIndex !== 'undefined' ){
            const width = listRef.current.props.width;
            const itemsPerRow = columnValue === 'auto' ? Math.floor(width / CARD_WIDTH) : Number(columnValue);
            const rowIndex = Math.floor(foundedPageIndex/itemsPerRow);
            listRef.current.scrollToItem(rowIndex, 'center');
          }
        }
      }, timeoutTime);
    } else {
      if ( listRef.current ){
        const foundedPageIndex = pages.findIndex((page:any) => page.documentId === Number(docId) && page.pageNum === Number(pageNum));
        if ( typeof foundedPageIndex !== 'undefined' ){
          const width = listRef.current.props.width;
          const itemsPerRow = columnValue === 'auto' ? Math.floor(width / CARD_WIDTH) : Number(columnValue);
          const rowIndex = Math.floor(foundedPageIndex/itemsPerRow);
          listRef.current.scrollToItem(rowIndex, 'center');
        }
      }
    }
  }

  if ( workspaceType !== 'pages' || !lastOpenedPage ) return null;
  if ( newDesignEnabled ) return (
    <Tooltip title={t('buttons.goToLastOpened')}>
      <IconButton
        name="Go to last opened"
        onClick={handleGoToLastOpenedPage}
      ><Icon icon="go_to_last_opened" /></IconButton>
    </Tooltip>
  );
  return (
    <Button
      onClick={handleGoToLastOpenedPage}
      color="primary"
      variant="outlined"
    >{t('buttons.goToLastOpened')}</Button>
  )
}

export default GoToLastOpenedPage;
